import { useState, useCallback } from "react";

import { authValidationService } from "../services/validationService";

export interface FormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

export interface FormErrors {
  email?: string;
  password?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: FormErrors;
}

export const useAuthForm = (initialData: Partial<FormData> = {}) => {
  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
    rememberMe: false,
    ...initialData,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = useCallback((): ValidationResult => {
    const validationResult = authValidationService.validateForm(formData);
    setErrors(validationResult.errors);
    return validationResult;
  }, [formData]);

  const handleChange = useCallback(
    (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value;

      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Clear error when user starts typing
      if (errors[field as keyof FormErrors]) {
        setErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }
    },
    [errors],
  );

  const reset = useCallback(() => {
    setFormData({
      email: "",
      password: "",
      rememberMe: false,
      ...initialData,
    });
    setErrors({});
    setIsSubmitting(false);
  }, [initialData]);

  return {
    formData,
    errors,
    isSubmitting,
    setIsSubmitting,
    handleChange,
    validate,
    reset,
  };
};
